import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const FLEET_SIZE = 6;
/** Three ships each: one big, two small. Short games, quick rematches. */
export const SHIP_SIZES = [3, 2, 2] as const;
export const FLEET_CELLS = SHIP_SIZES.reduce((total, size) => total + size, 0);

export interface FleetState extends BaseState {
	kind: "fleet";
	/** Each player's ships as lists of cells. The opponent's are redacted away. */
	ships: [number[][], number[][]];
	/** Cells each player has fired at, on the *other* board. */
	shots: [number[], number[]];
	/** Which of those shots hit. */
	hits: [number[], number[]];
	lastShot: { seat: PlayerIndex; cell: number; hit: boolean; sunk: boolean } | null;
}

export interface FleetMove {
	cell: number;
}

/** One attempt at dropping the fleet, or `null` if a ship found no room. */
function tryPlaceFleet(rng: Rng): number[][] | null {
	const taken = new Set<number>();
	const ships: number[][] = [];

	for (const size of SHIP_SIZES) {
		let landed = false;
		for (let attempt = 0; attempt < 200 && !landed; attempt++) {
			const horizontal = rng.next() < 0.5;
			const col = rng.int(horizontal ? FLEET_SIZE - size + 1 : FLEET_SIZE);
			const row = rng.int(horizontal ? FLEET_SIZE : FLEET_SIZE - size + 1);
			const cells: number[] = [];
			for (let i = 0; i < size; i++) {
				cells.push(horizontal ? row * FLEET_SIZE + col + i : (row + i) * FLEET_SIZE + col);
			}
			if (cells.some((cell) => taken.has(cell))) continue;
			for (const cell of cells) taken.add(cell);
			ships.push(cells);
			landed = true;
		}
		// Running out of attempts is astronomically unlikely on a 6×6 grid —
		// but "astronomically unlikely" is not "impossible", and quietly
		// sailing with a ship missing would be an unwinnable game.
		if (!landed) return null;
	}
	return ships;
}

/** Drops the fleet on the grid without overlaps, in a fixed, seeded way. */
function placeFleet(rng: Rng): number[][] {
	for (let attempt = 0; attempt < 100; attempt++) {
		// The rng stream moves on with every attempt, so a retry is a
		// genuinely different board, not the same dead end again.
		const ships = tryPlaceFleet(rng);
		if (ships) return ships;
	}
	// A hundred failed fleets in a row cannot happen by chance. Fall back to
	// one ship per row so the invariant holds no matter what.
	return SHIP_SIZES.map((size, row) =>
		Array.from({ length: size }, (_, i) => row * FLEET_SIZE + i),
	);
}

function isSunk(ship: number[], hits: number[]): boolean {
	return ship.every((cell) => hits.includes(cell));
}

export const fleetBattle: GameDefinition<FleetState, FleetMove> = {
	id: "fleet",
	category: "classics",
	title: "Fleet Battle",
	emoji: "🚢",
	blurb: "Three hidden ships each. Find your friend's before they find yours.",
	howTo: [
		"Your ships are hidden on your grid, and theirs on theirs.",
		"Tap a square on their grid to fire.",
		"A hit means you go again — a miss ends your turn.",
		"Sink all three of their ships to win.",
	],

	create(rng) {
		return {
			kind: "fleet",
			ships: [placeFleet(rng), placeFleet(rng)],
			shots: [[], []],
			hits: [[], []],
			lastShot: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const cell = (raw as { cell?: unknown }).cell;
		if (typeof cell !== "number" || !Number.isInteger(cell)) return null;
		if (cell < 0 || cell >= FLEET_SIZE * FLEET_SIZE) return null;
		return { cell };
	},

	apply(state, player, move): MoveOutcome<FleetState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.shots[player].includes(move.cell)) return reject("You've already fired there.");

		const enemy = otherPlayer(player);
		const enemyShips = state.ships[enemy];
		const hit = enemyShips.some((ship) => ship.includes(move.cell));

		const shots: [number[], number[]] = [[...state.shots[0]], [...state.shots[1]]];
		const hits: [number[], number[]] = [[...state.hits[0]], [...state.hits[1]]];
		shots[player].push(move.cell);
		if (hit) hits[player].push(move.cell);

		const sunk =
			hit && enemyShips.some((ship) => ship.includes(move.cell) && isSunk(ship, hits[player]));
		const scores: [number, number] = [...state.scores];
		if (hit) scores[player] += 1;

		const allSunk = enemyShips.every((ship) => isSunk(ship, hits[player]));
		return {
			ok: true,
			state: {
				...state,
				shots,
				hits,
				scores,
				lastShot: { seat: player, cell: move.cell, hit, sunk },
				// A hit earns another shot.
				turn: allSunk ? null : hit ? player : enemy,
				result: allSunk ? { winner: player } : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const fired = new Set(state.shots[player]);
		const moves: FleetMove[] = [];
		for (let cell = 0; cell < FLEET_SIZE * FLEET_SIZE; cell++) {
			if (!fired.has(cell)) moves.push({ cell });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state, viewer) {
		if (state.result) return state;
		const enemy = otherPlayer(viewer);
		const ships: [number[][], number[][]] = [[], []];
		ships[viewer] = state.ships[viewer];
		// Only ships you have already sunk are worth showing you.
		ships[enemy] = state.ships[enemy].filter((ship) => isSunk(ship, state.hits[viewer]));
		return { ...state, ships };
	},
};
